import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";

import {
    Injectable,
    inject
} from "@angular/core";

import {
    ICreateInternalApiClientService,
    IUpdateInternalApiClientService,
    InternalApiRequestService,
    IQueryInternalApiClientService,
    ListUtil
} from "@shopliftr/common-ng";
import {
    Report, ReportGenerationSubmission,
    SearchResponse,
    RequestUtil,
    GeneratedReport,
    SubmitRequest,
    UpdateRequest,
    DeleteResponse,
    BulkResponse,
    BulkUtil,
    CreateRequest
} from "@shopliftr/common-js/shared";
import {
    ModelAction, ISearchSort
} from "@shopliftr/common-js/data";
import { ReportSearchFilterOptions } from "@shopliftr/common-js/smart";

/**
 * API Client Service for Report operations.
 *
 * Handles CRUD operations, search, and report generation for Report entities.
 *
 * **Service Pattern:** API Client Service
 *
 * **Provision:** Singleton service provided in 'root'
 *
 * @implements {ICreateInternalApiClientService<Report>} Standard internal API create client pattern
 * @implements {IUpdateInternalApiClientService<Report>} Standard internal API update client pattern
 * @implements {IQueryInternalApiClientService<Report>} Standard internal API query client pattern
 *
 * @providedIn root
 */
@Injectable({
    providedIn: "root"
})
export class ReportApiClientService implements ICreateInternalApiClientService<Report>, IUpdateInternalApiClientService<Report>,
IQueryInternalApiClientService<Report> {

    /**
     * Base API path for report endpoints.
     */
    readonly basePath = "report";

    /**
     * Handles HTTP communication with Internal API for report operations.
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    search$(
        filterOptions: ReportSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<Report>> {

        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Report.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$(searchRequest, this.basePath);
    }

    /**
     * Retrieves multiple reports by their IDs in a single bulk request.
     *
     * @param ids - Array of report IDs to retrieve
     * @returns Observable of bulk response containing the requested reports
     * @see InternalApiRequestService.bulk
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<Report>(id, Report.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Creates multiple reports in a single bulk request.
     *
     * @param items - Array of create requests for reports
     * @returns Observable of bulk response containing the created reports
     * @see InternalApiRequestService.bulk
     */
    bulkCreate$(items: Array<CreateRequest<Report, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Updates multiple reports in a single bulk request.
     *
     * @param requests - Array of update requests for reports
     * @returns Observable of bulk response containing the updated reports
     * @see InternalApiRequestService.bulk
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Generates an instance of the report data from the ad events for the campaign
     * using the provided ReportGenerationSubmission parameters.
     */
    generateReport$(submissionPayload: ReportGenerationSubmission): Observable<GeneratedReport> {

        const submitRequest: SubmitRequest<ReportGenerationSubmission> = RequestUtil.createSubmitRequest(
            submissionPayload
        );
        return this._internalApiRequestService.submit$<ReportGenerationSubmission, GeneratedReport>(submitRequest, `${this.basePath}/generate`).pipe(map((response) => response.payload));
    }

    /**
     * List all reports associated with a campaign id.
     */
    getAll$(filterOptions: ReportSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<Report>> {

        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Report.modelName,
            0,
            100,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.getAll$(searchRequest, this.basePath);
    }

    /**
     * Update an existing report with the provided field changes.
     */
    update$(reportId: string, version: number, updateFields: Report, removeFields?: Array<string>): Observable<Report> {

        const fields = updateFields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<Report> = RequestUtil.createUpdateRequest(
            reportId,
            ModelAction.ClientUpdate,
            fields,
            version,
            removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Create and save a new report from the provided model.
     */
    create$(report: Report): Observable<Report> {

        const createRequest = RequestUtil.createCreateRequest<Report, void>(
            ModelAction.ClientCreate,
            report.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Delete an existing report.
     */
    delete$(reportId: string, version: number): Observable<DeleteResponse> {

        const deleteRequest = RequestUtil.createDeleteRequest<Report>(
            reportId,
            Report.modelName,
            ModelAction.ClientDelete,
            version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Get a report given its id
     */
    get$(reportId: string): Observable<Report> {

        const getRequest = RequestUtil.createGetRequest<Report>(
            reportId,
            Report.modelName,
            ModelAction.ClientGet
        );
        return this._internalApiRequestService.get$(getRequest, this.basePath).pipe(map((response) => response.payload as Report));
    }

    /**
     * Updates the record of the original report with the updated report provided
     */
    updateWithChanges$(original: Report, updated: Report): Observable<Report> {

        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }
}
